/**
 * Everything settling a closed trade WRITES, worked out before anything is written.
 *
 * ONE PLAN, TWO WRITERS. A commissioner's session settles a trade from the Trades page, and a
 * scheduled function settles the ones nobody's session was open for. Until now the second did not
 * exist, because the arithmetic lived inside the page — the ledger row, the stamp on the live
 * record, and the keeper reallocation 2015-III-7 forces when a keeper's pick leaves — and
 * `functions/` could not reach it. Moving it here means the two writers cannot disagree about
 * what a settled trade looks like, which is the property `resolve.ts` had to buy with a mirror
 * and a parity test.
 *
 * The plan is pure. It reads the declarations and the live trades it is handed and returns the
 * documents to save; the caller performs the writes in the order the plan lists them. The order
 * is the one the page always used, with the FAAB rows slotted in behind the ledger row — the
 * ledger row, the money, then the live record stamped with its id, then the slates — so a failure
 * part-way leaves the trade on the page as "waiting to be filed" with the button that retries it,
 * and never a settled trade with no row in the log or with its scrip still in the wrong hands.
 *
 * THE MECHANISM FOR A KEEPER WHOSE PICK JUST LEFT (Tim, 1 September: "a mechanism to then
 * reallocate any disrupted keepers, including ones that would need to be shifted up by
 * escalation"). `keeperEffect` over the ownership as it stands after this trade, with every
 * keeper's current round as his ceiling — a trade pushes a man EARLIER and never quietly slides
 * him later. Each move becomes `elevatedTo` on his declaration, which is the field that has
 * always meant "charged to this round, by choice", and the slate is saved with the status it
 * had. The GM keeps the choice the rule gives him: `elevatedTo` is a pin he can move on Declare.
 *
 * KEEPERS CHANGE HANDS WITH THEIR STANDING (2013-III-3, 2018-III-2). A declared keeper sent in
 * the deal comes off this slate; one received goes on it at the round he already costs, carrying
 * his tag if he had one. Neither GM may un-keep him after the deadline, so this is not a choice
 * the app makes for anybody; it is the rule.
 *
 * WHO IS TOLD. An audit row per GM whose slate moved, `onBehalfOf` him, naming the trade and
 * every move. A submitted slate changing with its owner nowhere near it is exactly the kind of
 * edit that has to leave a paper trail. Nothing here can refuse: the form refused an unpayable
 * trade before it was filed. If the ledger has drifted enough that a fit no longer exists, the
 * slate is left as it was and the note says so, because a silent no-op is the one outcome worse
 * than either alternative.
 */

import type {
  AuditEntry, FaabEntry, GmId, KeeperDeclaration, LiveTradeDoc, Trade,
} from '../store/types.js';
import { applyPickTrades } from './board.js';
import { ownedRoundsFor, ownership2026 } from './picks2026.js';
import { shortfallSays } from './keeperSlots.js';
import { managerShort } from './identity.js';
import { settlementFor } from './tradeWindow.js';
import { auditEntry } from './auditEntry.js';
import { tradeFaabEntries, tradeFaabGaps } from './tradeFaab.js';
import {
  chargedNow, keeperEffect, keepersAfterTrade, keepersSent, movesSay, pickTransfersFrom,
  pricedSlate, type PricedRow,
} from './tradeKeepers.js';

/** "RB Bijan Robinson" → "Bijan Robinson". Mirrors `ui/TradeAssets.ts#bareName`. */
const POS_PREFIX = /^(QB|RB|WR|TE|K|DEF|IDP)\s+(.+)$/;
export const bareName = (text: string): string => POS_PREFIX.exec(text.trim())?.[2] ?? text;

export interface SettleInput {
  /** The trade being settled, as it stands (lease and all). */
  trade: LiveTradeDoc;
  /** Every declaration for the season the caller may read. */
  declarations: readonly KeeperDeclaration[];
  /** Every live trade for the season, including this one. */
  liveTrades: readonly LiveTradeDoc[];
  /**
   * The FAAB ledger as it stands.
   *
   * Handed in rather than read here because the plan is pure, and needed at all because the FAAB
   * rows are DERIVED FROM THE GAP between what this trade says and what the ledger already holds
   * for it — see `domain/tradeFaab.ts`. That is what makes a retry safe: a second settle of a trade
   * whose money already moved computes no rows, rather than computing the same ids again and being
   * refused by rules that forbid a rewrite.
   */
  faabLedger: readonly FaabEntry[];
  /** The keeper price list the slates are priced from. */
  rows: readonly PricedRow[];
  gmOfTeam: (team: string) => string;
  draftRounds: number;
  /** Who is recording it — a commissioner's id, or the schedule's. */
  actor: string;
  /** ISO. */
  at: string;
}

export interface SettlePlan {
  /** The ledger row. Written first. */
  ledger: Trade;
  /**
   * The FAAB that changed hands, one row per man whose balance moves. Written SECOND — before the
   * live record is stamped — because the stamp is what takes the trade off the list of things
   * waiting to be filed. Money after the stamp is money nothing retries.
   */
  faab: FaabEntry[];
  /** The live record stamped with the ledger id and its lease removed. Written third. */
  live: LiveTradeDoc;
  /** Slates that moved. Written after the record, in this order. */
  declarations: KeeperDeclaration[];
  /** Rows for the audit log, one per slate touched or left short. Fire-and-forget. */
  notes: AuditEntry[];
}

export function settlePlan(input: SettleInput): SettlePlan {
  const { trade: t, actor, at } = input;
  const ledger = settlementFor(t, actor.toUpperCase(), at) as Trade;
  const { settling: _held, ...rest } = t;
  const live: LiveTradeDoc = { ...rest, settledAs: ledger.id };
  const { declarations, notes } = reallocateKeepers(input);
  /*
   * FAAB IS AN ASSET AND NOW MOVES LIKE ONE (Tim, 9 September: "i had FAAB in trades that should
   * have been deducted/added as necessary once completed. I made manual adjustments instead").
   * `settlementFor` has always crossed the scrip over into `sides[i].faab` and nothing had ever
   * turned that into a balance.
   */
  const faab = tradeFaabEntries(
    tradeFaabGaps([ledger], input.faabLedger, ledger.season),
    ledger.season,
    actor.toUpperCase() as GmId,
    at,
  );
  return { ledger, faab, live, declarations, notes };
}

function reallocateKeepers(input: SettleInput): Pick<SettlePlan, 'declarations' | 'notes'> {
  const { trade: t, rows, gmOfTeam, draftRounds, actor, at } = input;
  const season = t.season;
  const decls = input.declarations;
  const trades = input.liveTrades;
  const out: KeeperDeclaration[] = [];
  const notes: AuditEntry[] = [];

  const after = applyPickTrades(
    ownership2026(gmOfTeam),
    pickTransfersFrom(trades.map((x) => (x.id === t.id ? { ...x, settledAs: x.settledAs ?? 'pending' } : x)), season),
  );
  /* Each side's declared keepers as they stand, before anybody moves. */
  const slates = t.sides.map((side) => {
    const gm = side.gm.toUpperCase();
    const decl = decls.find((d) => d.gm.toUpperCase() === gm);
    return { gm, decl, keepers: pricedSlate(gm, decl, rows, gmOfTeam) };
  });

  for (const [i, side] of t.sides.entries()) {
    const { gm, decl } = slates[i]!;
    const other = slates[1 - i]!;
    const outgoing = keepersSent(side.sends, slates[i]!.keepers).map((k) => k.player);
    const incoming = keepersSent(t.sides[1 - i]!.sends, other.keepers);
    const keepers = keepersAfterTrade(slates[i]!.keepers, outgoing, incoming);
    if (!decl && incoming.length === 0) continue;
    if (keepers.length === 0 && outgoing.length === 0) continue;

    const owned = ownedRoundsFor(gm, after, draftRounds);
    /* `after` already reflects this trade, so the effect is measured with nothing to give or get. */
    const before = ownedRoundsFor(gm, applyPickTrades(ownership2026(gmOfTeam),
      pickTransfersFrom(trades.filter((x) => x.id !== t.id), season)), draftRounds);
    const now = chargedNow(slates[i]!.keepers, decl, before);
    /* The GM's own answer to "who moves", if he gave one on the affirmation card. */
    const effect = keeperEffect(gm, keepers, now, owned, [], [], t.keeperPins?.[gm] ?? {});
    const label = `trade with ${managerShort(other.gm)}`;

    if (effect.shortfall) {
      notes.push(auditEntry(season, actor, {
        at, kind: 'keeper', onBehalfOf: gm, target: `liveTrades/${t.id}`,
        what: `${managerShort(gm)}’s keepers no longer fit after the ${label}: ${shortfallSays(effect.shortfall)}`,
      }));
      continue;
    }
    const changed = effect.moves.length > 0 || outgoing.length > 0 || incoming.length > 0;
    if (!changed) continue;

    const moved = new Map(effect.moves.map((m) => [m.player, m.to]));
    const gone = new Set(outgoing);
    const taggedThere = new Set(other.decl?.keepers.filter((k) => k.franchiseTagged).map((k) => k.player) ?? []);
    const base: KeeperDeclaration = decl ?? { season, gm, keepers: [], status: 'submitted', updatedAt: at };
    out.push({
      ...base,
      keepers: [
        ...base.keepers
          .filter((k) => !gone.has(k.player))
          .map((k) => (moved.has(k.player) ? { ...k, elevatedTo: moved.get(k.player)! } : k)),
        ...incoming.map((k) => ({
          player: k.player,
          ...(taggedThere.has(k.player) ? { franchiseTagged: true } : {}),
          ...(moved.has(k.player) ? { elevatedTo: moved.get(k.player)! } : {}),
        })),
      ],
      updatedAt: at,
      updatedBy: actor.toUpperCase(),
    });
    const said = [
      outgoing.length ? `${outgoing.map(bareName).join(', ')} traded away` : '',
      effect.moves.length ? movesSay(effect.moves, bareName) : '',
    ].filter(Boolean).join('; ');
    notes.push(auditEntry(season, actor, {
      at, kind: 'keeper', onBehalfOf: gm, target: `liveTrades/${t.id}`,
      what: `${managerShort(gm)}’s keepers after the ${label}: ${said}`,
      detail: { rule: '2015-III-7' },
    }));
  }
  return { declarations: out, notes };
}
